import classNames from 'classnames';
import { type CSSProperties, forwardRef, type HTMLAttributes } from 'react';

import styles from './AIAssistantSparkleDisc.module.css';

/**
 * The AI Assistant sparkle glyph. Fills its box and paints with
 * `currentColor` so the surrounding disc controls the ink per theme.
 */
const AISparkle = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="9.3984 9.4 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M27.1312 15.9494C27.3326 15.337 28.1989 15.337 28.4003 15.9494L30.9667 23.7528C31.033 23.9543 31.191 24.1123 31.3925 24.1786L39.1959 26.745C39.8083 26.9464 39.8083 27.8127 39.1959 28.0141L31.3925 30.5805C31.191 30.6467 31.033 30.8048 30.9667 31.0063L28.4003 38.8097C28.1989 39.422 27.3326 39.422 27.1312 38.8097L24.5648 31.0063C24.4986 30.8048 24.3405 30.6467 24.139 30.5805L16.3356 28.0141C15.7233 27.8127 15.7233 26.9464 16.3356 26.745L24.139 24.1786C24.3405 24.1123 24.4986 23.9543 24.5648 23.7528L27.1312 15.9494Z"
      fill="currentColor"
    />
  </svg>
);

export interface AIAssistantSparkleDiscProps
  extends HTMLAttributes<HTMLElement> {
  /** Diameter of the disc as a CSS length string (e.g. "36px" or "1.5rem"). */
  size: string;
  /** The FAB renders the disc as its own button; everything else is inert. */
  as?: 'span' | 'button';
}

/**
 * The AI Assistant icon: a themed circle (light-blue in light mode, paler
 * blue in dark) holding the sparkle glyph, whose ink swaps white↔dark ink
 * per theme. Single source of truth for both the floating FAB and the
 * in-chat assistant avatar so the two can never drift apart.
 */
// forwardRef so RevelioTooltip can inject its anchor ref onto the underlying
// DOM node — a plain function component would drop it and the FAB tooltip
// would mis-position.
export const AIAssistantSparkleDisc = forwardRef<
  HTMLElement,
  AIAssistantSparkleDiscProps
>(function AIAssistantSparkleDisc(
  { size, as: Component = 'span', className, ...rest },
  ref
) {
  return (
    <Component
      ref={ref as never}
      className={classNames(styles.disc, className)}
      style={{ '--sparkle-disc-size': size } as CSSProperties}
      {...rest}
    >
      <AISparkle />
    </Component>
  );
});
